export type LocalSpeechStatus = 'available' | 'downloadable' | 'downloading' | 'unavailable' | 'unsupported';
type Options = { langs:string[]; processLocally:true };
export type LocalSpeechConstructor = {
  prototype: object;
  available?:(options:Options)=>Promise<Exclude<LocalSpeechStatus,'unsupported'>>;
  install?:(options:Options)=>Promise<boolean>;
};
export async function localSpeechState(ctor:LocalSpeechConstructor | undefined):Promise<{status:LocalSpeechStatus;lang:string}> {
  if(!ctor?.available || !('processLocally' in ctor.prototype))return {status:'unsupported',lang:'en-GB'};
  try {
    const uk=await ctor.available({langs:['en-GB'],processLocally:true});
    if(uk!=='unavailable')return {status:uk,lang:'en-GB'};
    return {status:await ctor.available({langs:['en-US'],processLocally:true}),lang:'en-US'};
  } catch {return {status:'unavailable',lang:'en-GB'};}
}
export async function installLocalSpeech(ctor:LocalSpeechConstructor | undefined,online:boolean):Promise<{status:LocalSpeechStatus;lang:string}> {
  const state=await localSpeechState(ctor);
  if(state.status==='available')return state;
  if(!online)throw new Error('Connect to the Internet once to prepare offline speech.');
  if(!ctor?.install || !['downloadable','downloading'].includes(state.status))throw new Error('This browser does not provide offline English speech. You can type or use your phone keyboard’s dictation.');
  if(!await ctor.install({langs:[state.lang],processLocally:true}))throw new Error('The speech download did not finish. Try again while connected.');
  const installed=await localSpeechState(ctor);
  if(installed.status!=='available')throw new Error('Offline speech is not ready yet. Try preparation again while connected.');
  return installed;
}

export type SpeechPreparationState = {
  status: LocalSpeechStatus | 'checking'; lang: string; preparing: boolean; error: string;
};

// One download per app session, shared by every field and header. Reconnecting
// permits a failed download to retry; reopening a panel never starts another one.
export function createSpeechPreparation() {
  let snapshot: SpeechPreparationState = {status:'checking',lang:'en-GB',preparing:false,error:''};
  let inFlight: Promise<void> | null = null;
  let attempted = false;
  const listeners = new Set<() => void>();
  const publish = (next: SpeechPreparationState) => { snapshot=next; listeners.forEach(listener=>listener()); };
  function prepare(ctor: LocalSpeechConstructor | undefined, online: boolean, retry = false): Promise<void> {
      if (inFlight) return retry ? inFlight.then(()=>prepare(ctor,online,true)) : inFlight;
      inFlight = (async () => {
        const state = await localSpeechState(ctor);
        const canDownload = ['downloadable','downloading'].includes(state.status);
        if (state.status === 'available') attempted=false;
        publish({...state,preparing:false,error:canDownload && online ? snapshot.error : ''});
        if (!online || !canDownload || !ctor?.install || attempted && !retry) return;
        attempted=true;
        publish({...state,preparing:true,error:''});
        try {
          const installed = await installLocalSpeech(ctor,online);
          attempted=false;
          publish({...installed,preparing:false,error:''});
        } catch {
          const current = await localSpeechState(ctor);
          publish({...current,preparing:false,error:current.status==='available' ? '' : 'Speech preparation did not finish. It will retry when you reconnect.'});
        }
      })().finally(()=>{inFlight=null;});
      return inFlight;
  }
  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    prepare,
  };
}
